import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import { DateTime } from 'luxon';
import { Check, X, Loader2, Clock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SuddenLeaveReport } from '@/hooks/useSuddenLeaveReports';

interface SuddenLeaveTableProps {
  reports: SuddenLeaveReport[];
  isLoading: boolean;
  onReview: (report: SuddenLeaveReport) => void;
}

function formatClassTime(startDate: string | null, endDate: string | null): string | null {
  if (!startDate || !endDate) return null;
  const start = DateTime.fromISO(startDate).setZone('Asia/Bangkok');
  const end = DateTime.fromISO(endDate).setZone('Asia/Bangkok');
  return `${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')}`;
}

function formatReviewedAt(reviewedAt: string | null): string {
  if (!reviewedAt) return '';
  return format(new Date(reviewedAt), 'dd/MM/yy HH:mm', { locale: th });
}

export function SuddenLeaveTable({ reports, isLoading, onReview }: SuddenLeaveTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        ไม่พบข้อมูลการลากะทันหันในช่วงเวลานี้
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">วันที่</TableHead>
              <TableHead>นักเรียน</TableHead>
              <TableHead>ครู</TableHead>
              <TableHead>วิชา</TableHead>
              <TableHead>สถานที่</TableHead>
              <TableHead className="w-[140px]">สถานะการจ่าย</TableHead>
              <TableHead>หมายเหตุ</TableHead>
              <TableHead className="w-[100px] text-center">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report) => {
              const isReviewed = report.sudden_leave_reviewed_at !== null;
              
              return (
                <TableRow key={report.id}>
                  <TableCell className="font-medium">
                    <div>
                      {format(new Date(report.teaching_date), 'dd/MM/yy', { locale: th })}
                      {formatClassTime(report.start_date, report.end_date) && (
                        <div className="text-xs text-muted-foreground">
                          {formatClassTime(report.start_date, report.end_date)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{report.student_id} {report.student_nickname}</div>
                      <div className="text-xs text-muted-foreground">{report.student_full_name}</div>
                    </div>
                  </TableCell>
                  <TableCell>{report.teacher_display || report.teacher_id}</TableCell>
                  <TableCell>{report.subject} {report.student_grade_level}</TableCell>
                  <TableCell>{report.teaching_location}</TableCell>
                  <TableCell>
                    {!isReviewed ? (
                      <Badge variant="outline" className="gap-1 bg-yellow-50 text-yellow-700 border-yellow-300">
                        <Clock className="h-3 w-3" />
                        รอการตรวจสอบ
                      </Badge>
                    ) : report.sudden_leave ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="destructive" className="gap-1 cursor-help">
                            <X className="h-3 w-3" />
                            ไม่จ่าย
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>ตรวจสอบโดย: {report.sudden_leave_reviewed_by}</p>
                          <p>เมื่อ: {formatReviewedAt(report.sudden_leave_reviewed_at)}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="default" className="gap-1 bg-green-600 cursor-help">
                            <Check className="h-3 w-3" />
                            จ่ายปกติ
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>ตรวจสอบโดย: {report.sudden_leave_reviewed_by}</p>
                          <p>เมื่อ: {formatReviewedAt(report.sudden_leave_reviewed_at)}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {report.sudden_leave_admin_note || '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReview(report)}
                    >
                      {isReviewed ? 'แก้ไข' : 'ตรวจสอบ'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
